/** Supernova's capsules and what they do (docs/spec/03 §2, §3). */

import { sin } from './math';
import { addScore } from './score';
import { CAPSULE_WEIGHTS, FIELD, FIGHTER, LIFE_CAPSULE_FROM, NOVA, SCORE } from './tuning';
import type { CapsuleKind, Enemy, GameState, Powers } from './types';

export function newPowers(novas: number): Powers {
  return { rapid: 0, spread: 0, pierce: 0, chrono: 0, shield: false, novas };
}

/** Draw a capsule kind: by weight, never the same twice running, nothing that would be wasted. */
function drawKind(s: GameState): CapsuleKind {
  const allowed = CAPSULE_WEIGHTS.filter(([kind]) => {
    if (kind === s.lastCapsule) return false;
    if (kind === 'life') return s.stage >= LIFE_CAPSULE_FROM && s.lives < SCORE.maxLives;
    if (kind === 'nova') return s.powers.novas < NOVA.maxNovas;
    if (kind === 'shield') return !s.powers.shield;
    return true;
  });
  let total = 0;
  for (const [, w] of allowed) total += w;
  let roll = s.rng.int(Math.max(1, total));
  for (const [kind, w] of allowed) {
    if (roll < w) return kind;
    roll -= w;
  }
  return 'rapid';
}

export function spawnCapsule(s: GameState, x: number, y: number): void {
  const kind = drawKind(s);
  s.lastCapsule = kind;
  const baseX = Math.min(FIELD.w - 16, Math.max(16, x));
  s.capsules.push({ kind, baseX, x: baseX, y, t: 0 });
  s.events.push({ type: 'capsule', x: baseX, y, kind });
}

/**
 * A kill feeds the drop meter, one that was out on a dive twice over; the kill that fills it
 * drops a capsule where the enemy was (03 §2).
 */
export function dropOnKill(s: GameState, e: Enemy, aloft: boolean): void {
  if (s.phase === 'challenge') return;
  s.dropMeter += aloft ? 2 : 1;
  if (s.dropMeter < NOVA.dropAt) return;
  s.dropMeter = 0;
  spawnCapsule(s, e.x, Math.max(24, e.y));
}

function take(s: GameState, kind: CapsuleKind, x: number, y: number): void {
  const pw = s.powers;
  switch (kind) {
    case 'rapid': pw.rapid = NOVA.timed; break;
    case 'spread': pw.spread = NOVA.timed; break;
    case 'pierce': pw.pierce = NOVA.timed; break;
    case 'chrono': pw.chrono = NOVA.chronoTicks; break;
    case 'shield': pw.shield = true; break;
    case 'nova': pw.novas = Math.min(NOVA.maxNovas, pw.novas + 1); break;
    case 'life': if (s.lives < SCORE.maxLives) s.lives++; break;
  }
  addScore(s, NOVA.capsulePoints);
  s.events.push({ type: 'powerUp', x, y, kind });
}

export function updateCapsules(s: GameState): void {
  if (s.capsules.length === 0) return;
  const p = s.player;
  const canTake = p.status === 'flying' || p.status === 'arriving';
  const reach = NOVA.capsuleHalf + FIGHTER.hitW + (p.dual ? FIGHTER.dualOffset : 0);
  s.capsules = s.capsules.filter((c) => {
    c.t++;
    c.y += NOVA.capsuleSpeed;
    c.x = c.baseX + sin(c.t / 14) * NOVA.capsuleSway;
    if (canTake && Math.abs(c.x - p.x) <= reach && Math.abs(c.y - FIELD.fighterY) <= NOVA.capsuleHalf + FIGHTER.hitH) {
      take(s, c.kind, c.x, c.y);
      return false;
    }
    return c.y < FIELD.h + 8;
  });
}

const TIMED = ['rapid', 'spread', 'pierce', 'chrono'] as const;

/** Run the timed power-ups down. */
export function tickPowers(s: GameState): void {
  const pw = s.powers;
  for (const kind of TIMED) {
    if (pw[kind] > 0 && --pw[kind] === 0) s.events.push({ type: 'powerDown', kind });
  }
}

/** Everything but the Nova stock goes with the ship (03 §2). */
export function losePowers(s: GameState): void {
  const pw = s.powers;
  pw.rapid = 0;
  pw.spread = 0;
  pw.pierce = 0;
  pw.chrono = 0;
  pw.shield = false;
}
